import { useState, useEffect } from 'react'
import { supabase } from '../services/supabase'

interface LowStockItem {
  id: string
  name: string
  type: 'product' | 'variant'
  productName: string
  variantInfo?: string
  currentStock: number
  threshold: number
  productId: string
}

export default function LowStockAlerts() {
  const [lowStockItems, setLowStockItems] = useState<LowStockItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchLowStockItems()
  }, [])

  const fetchLowStockItems = async () => {
    try {
      setLoading(true)
      const items: LowStockItem[] = []

      // Fetch all products and filter for low stock (stock > 0 AND stock <= threshold)
      const { data: allProducts, error: productsError } = await supabase
        .from('products')
        .select('id, name, stock, low_stock_threshold')
        .gt('stock', 0)

      if (productsError) throw productsError

      allProducts?.forEach(p => {
        if (p.stock <= p.low_stock_threshold) {
          items.push({
            id: p.id,
            name: p.name,
            type: 'product',
            productName: p.name,
            currentStock: p.stock,
            threshold: p.low_stock_threshold,
            productId: p.id
          })
        }
      })

      // Fetch all variants with their product names
      const { data: allVariants, error: variantsError } = await supabase
        .from('product_variants')
        .select(`
          id,
          variant_name,
          variant_value,
          stock_quantity,
          low_stock_threshold,
          product_id,
          products(name)
        `)
        .gt('stock_quantity', 0)

      if (variantsError) throw variantsError

      allVariants?.forEach(v => {
        if (v.stock_quantity <= v.low_stock_threshold) {
          // Handle both single object and array responses from join
          const productsData = v.products as { name: string } | { name: string }[] | null
          const productName = Array.isArray(productsData)
            ? productsData[0]?.name || 'Unknown Product'
            : productsData?.name || 'Unknown Product'
          items.push({
            id: v.id,
            name: `${productName} - ${v.variant_name}: ${v.variant_value}`,
            type: 'variant',
            productName: productName,
            variantInfo: `${v.variant_name}: ${v.variant_value}`,
            currentStock: v.stock_quantity,
            threshold: v.low_stock_threshold,
            productId: v.product_id
          })
        }
      })

      // Sort by stock level (lowest first)
      items.sort((a, b) => a.currentStock - b.currentStock)

      setLowStockItems(items)
    } catch (error) {
      console.error('Error fetching low stock items:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-12 bg-gray-100 rounded"></div>
            <div className="h-12 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  if (lowStockItems.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-500">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          Low Stock Alerts
        </h3>
        <div className="text-center py-8">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-green-600">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          </div>
          <p className="text-sm text-gray-500">All products are well-stocked!</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-orange-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-orange-100 bg-orange-50">
        <h3 className="text-base font-semibold text-orange-800 flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-orange-500">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          Low Stock Alerts ({lowStockItems.length})
        </h3>
      </div>

      <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
        {lowStockItems.map((item) => (
          <div key={`${item.type}-${item.id}`} className="px-5 py-3 hover:bg-gray-50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {item.productName}
                </p>
                {item.variantInfo && (
                  <p className="text-xs text-purple-600">{item.variantInfo}</p>
                )}
              </div>
              <div className="flex items-center gap-3 ml-4">
                <div className="text-right">
                  <p className="text-sm font-semibold text-orange-600">
                    {item.currentStock} left
                  </p>
                  <p className="text-xs text-gray-400">
                    Threshold: {item.threshold}
                  </p>
                </div>
                <span className="badge-low-stock">Low Stock</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
